import type { MarkOptional } from 'node_modules/ts-essentials/dist';
import {
  useEffect, useState,
} from 'react';
import { MAX_GROUP_COUNT } from '~/_shared/constants/grouping.constants';
import type {
  ColumnId, SpreadsheetColumn,
} from '~/_shared/types/spreadsheetData/spreadsheetColumn';
import { SERVER_ADDED_COLUMN_NAMES } from '~/_shared/types/spreadsheetData/spreadsheetColumns.helpers';
import { usePrevious } from '~/_shared/utils/hooks/usePrevious';
import { isColumnNumericFromNumberResponse } from '~/_shared/utils/spreadsheet/useIsNumericColumnCheck.helpers';
import { isNumber } from '~/_shared/utils/typeGuards';
import { BASE_MAPS_COLUMN_ID } from '~/map/layered/layering.repository';
import {
  getSpreadsheetBulkData, type SpreadsheetColumnsToFetchGroupResponse, type SpreadsheetColumnsToFetchNumberResponse,
  type SpreadsheetDataBulkRequestGetter, type SpreadsheetDataBulkResponse,
} from '~/spreadsheet/spreadsheet.repository';
import { isSuccessBulkSpreadsheet } from '~/spreadsheet/typeguards';
import { useSpreadsheetColumns } from '~/store/matchupData/matchupDataSelectors.hook';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';
import { generateGroupIdByName } from '~/store/spreadsheetData/grouping/spreadsheetData.grouping.helpers';
import { DataType } from '~/store/spreadsheetData/spreadsheetData.state';
import { translate } from '~/translations/Trans';
import { LOCATION_COUNT_ID } from './modalSteps/constraintsValidation/constraints.repository';

export const EMPTY_GROUP_ID = '__EMPTY__';

export type ATOColumnMetric = Readonly<{
  columnId: ColumnId;
  name: string;
  isNumeric: boolean;
  numericPercent: number;
  groupId?: string;
}>;

type ResponseColumnData = Readonly<{
  column: SpreadsheetColumn;
  numericDetails: SpreadsheetColumnsToFetchNumberResponse;
  groupDetails: SpreadsheetColumnsToFetchGroupResponse | undefined;
}>;

type ResponseGroupColumnData = ResponseColumnData & Readonly<{
  groupDetails: SpreadsheetColumnsToFetchGroupResponse;
}>;

export const useGetColumnATOMetrics = (spreadsheetId: number | null,
  { includeGroups }: { includeGroups?: boolean } = {}
) => {
  const clientId = useClientIdSelector();
  const mapId = useMapIdSelector();
  const [isLoading, setIsLoading] = useState(false);
  const matchupColumns = useSpreadsheetColumns();
  const [columnMetrics, setColumnMetrics] = useState<readonly ATOColumnMetric[]>([]);
  const [rowsCount, setRowsCount] = useState(0);

  const prevMatchupColumns = usePrevious(matchupColumns);

  useEffect(() => {
    const columnsChanged = !prevMatchupColumns || prevMatchupColumns.length !== matchupColumns.length ||
      prevMatchupColumns.some((col, i) => col.id !== matchupColumns[i]?.id);

    if (!columnsChanged) {
      return;
    }

    if (spreadsheetId) {
      if (clientId === null || mapId === null) {
        console.error(`Either clientId (${clientId}) or mapId (${mapId}) is null when checking column is numeric.`);
        return;
      }

      const columns = Object.fromEntries(
        matchupColumns
          .filter(column => column.id !== BASE_MAPS_COLUMN_ID && !SERVER_ADDED_COLUMN_NAMES.has(column.name))
          .map((column) => ([column.id, {}]))
      );

      const params: SpreadsheetDataBulkRequestGetter[] = [{
        map_id: mapId,
        spreadsheet_id: spreadsheetId,
        exclude_basic_data: true,
        exclude_row_data: includeGroups ? false : true,
        columns_to_fetch: {
          [DataType.NUMBER]: columns,
          ...(includeGroups ? { [DataType.GROUP]: columns } : undefined),
        },
      }];

      setIsLoading(true);
      getSpreadsheetBulkData(clientId, { params })
        .then(response => {
          const spreadsheet = response?.data?.[0];
          if (spreadsheet && isSuccessBulkSpreadsheet(spreadsheet)) {
            const metrics = createATOMetricsFromSpreadsheetData(matchupColumns, spreadsheet);

            setColumnMetrics(metrics);
            setRowsCount(spreadsheet.result?.order_list?.length ?? 0);
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [spreadsheetId, clientId, mapId, matchupColumns, prevMatchupColumns, includeGroups]);

  return {
    isLoading,
    columnMetrics,
    rowsCount,
  };
};

const normalizeGroupIdFromBackend = (groupId: string): string =>
  groupId === '' ? EMPTY_GROUP_ID : generateGroupIdByName(groupId);

const validateResponseData = (data: MarkOptional<ResponseColumnData, 'numericDetails'>): data is ResponseColumnData => {
  const { numericDetails: details } = data;
  return !!details && typeof details.min === 'number' && typeof details.max === 'number' && details.min <= details.max;
};

const isColumnNumeric = (data: ResponseColumnData): boolean => isColumnNumericFromNumberResponse(data.column.id, data.numericDetails);

const isColumnGroup = (data: ResponseColumnData): data is ResponseGroupColumnData => {
  const groupCount = data.groupDetails?.unique_values?.length;
  const isGroup = !isColumnNumeric(data) && isNumber(groupCount) && groupCount < MAX_GROUP_COUNT && groupCount > 1;

  return isGroup;
};

const createNumericalMetrics = ({ numericDetails, column }: ResponseColumnData): readonly ATOColumnMetric[] => [{
  columnId: column.id,
  name: column.name,
  isNumeric: true,
  numericPercent: numericDetails.numeric_and_empty_percent,
}];

const createGroupMetrics = ({ groupDetails, numericDetails, column }: ResponseGroupColumnData): readonly ATOColumnMetric[] => {
  const uniqueValues = groupDetails.unique_values;
  const uniqueValuesFormatted = groupDetails.unique_values_formatted;

  return uniqueValues.flatMap((groupName, index) => ({
    columnId: column.id,
    name: uniqueValuesFormatted?.[index] ?? groupName,
    isNumeric: false,
    numericPercent: numericDetails.numeric_and_empty_percent,
    groupId: normalizeGroupIdFromBackend(groupName),
  }));
};

const createOtherMetrics = ({ numericDetails, column }: ResponseColumnData): readonly ATOColumnMetric[] => [{
  columnId: column.id,
  name: column.name,
  isNumeric: false,
  numericPercent: numericDetails.numeric_and_empty_percent,
}];

const createLocationCountMetric = (): ATOColumnMetric => ({
  columnId: LOCATION_COUNT_ID,
  name: translate('locationCount'),
  isNumeric: true,
  numericPercent: 100,
});

const createATOMetricsFromSpreadsheetData = (
  matchupColumns: SpreadsheetColumn[],
  spreadsheet: SpreadsheetDataBulkResponse
): ATOColumnMetric[] => {
  const responseColumnsData = matchupColumns.map(column => ({
    column,
    numericDetails: spreadsheet.result?.columns_to_fetch?.[DataType.NUMBER]?.[column.id],
    groupDetails: spreadsheet.result?.columns_to_fetch?.[DataType.GROUP]?.[column.id],
  }))
    .filter(validateResponseData);

  const columnMetrics = responseColumnsData.flatMap(data => {
    if (isColumnNumeric(data)) {
      return createNumericalMetrics(data);
    }
    else if (isColumnGroup(data)) {
      return createGroupMetrics(data);
    }
    else {
      return createOtherMetrics(data);
    }
  });

  const allMetrics = [createLocationCountMetric(), ...columnMetrics];
  return allMetrics;
};
